/* CUSTOM: Product page controllers — rerender dispatcher, gallery wheel navigation, badges over the
   gallery, color picker position, sticky add-to-cart bar with size sheet, single-open accordions.
   Loaded on product pages only from snippets/custom-head-assets.liquid; runs after theme.js. */
(() => {
  // --- CUSTOM SCRIPT START: Product update dispatcher ---
  // Fires `custom:product-updated` on document once per frame after `variant:change` (bubbles),
  // `product:rerender` (does not bubble; the capture listener fires BEFORE the theme swaps the DOM)
  // or `shopify:section:load`. Consumers therefore always see the swapped markup and run once.
  let productUpdateFrame = 0;

  const dispatchProductUpdated = (event) => {
    if (productUpdateFrame) return;

    productUpdateFrame = requestAnimationFrame(() => {
      productUpdateFrame = 0;
      document.dispatchEvent(new CustomEvent("custom:product-updated", { detail: { source: event.type } }));
    });
  };

  document.addEventListener("variant:change", dispatchProductUpdated);
  document.addEventListener("product:rerender", dispatchProductUpdated, true);
  document.addEventListener("shopify:section:load", dispatchProductUpdated);
  // --- CUSTOM SCRIPT END: Product update dispatcher ---

  // --- CUSTOM SCRIPT START: Product gallery wheel navigation (desktop) ---
  // One slide per horizontal wheel impulse on the PDP gallery (>= 1000px); vertical input is left to the page.
  // Delegated on document so it survives the rerender on variant change; registered only when a gallery exists.
  const PRODUCT_GALLERY_WHEEL_SELECTOR = ".product > product-gallery .product-gallery__carousel";
  const PRODUCT_GALLERY_WHEEL_MIN_DELTA = 40; // accumulated deltaX needed to advance
  const PRODUCT_GALLERY_WHEEL_COOLDOWN = 400; // ms, blocks trackpad momentum
  const PRODUCT_GALLERY_WHEEL_RESET = 200; // ms without input resets the accumulator
  const productGalleryDesktop = window.matchMedia("(min-width: 1000px)");
  const productGalleryWheelState = new WeakMap();

  function onProductGalleryWheel(event) {
    if (!productGalleryDesktop.matches) {
      return;
    }

    const carousel = event.target.closest?.(PRODUCT_GALLERY_WHEEL_SELECTOR);

    if (!carousel || typeof carousel.next !== "function") {
      return;
    }

    // Vertical intent: let the page scroll instead of hijacking it.
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
      return;
    }

    event.preventDefault();

    const now = performance.now();
    const state = productGalleryWheelState.get(carousel) || { delta: 0, lockedUntil: 0, timeout: 0 };

    productGalleryWheelState.set(carousel, state);

    if (now < state.lockedUntil) {
      return;
    }

    state.delta += event.deltaX;
    clearTimeout(state.timeout);
    state.timeout = setTimeout(() => {
      state.delta = 0;
    }, PRODUCT_GALLERY_WHEEL_RESET);

    if (Math.abs(state.delta) < PRODUCT_GALLERY_WHEEL_MIN_DELTA) {
      return;
    }

    if (state.delta > 0) {
      carousel.next();
    } else {
      carousel.previous();
    }

    state.delta = 0;
    state.lockedUntil = now + PRODUCT_GALLERY_WHEEL_COOLDOWN;
  }

  if (document.querySelector(PRODUCT_GALLERY_WHEEL_SELECTOR)) {
    document.addEventListener("wheel", onProductGalleryWheel, { passive: false });
  }
  // --- CUSTOM SCRIPT END: Product gallery wheel navigation (desktop) ---

  // --- CUSTOM SCRIPT START: Badges over the media gallery ---
  // Moves the theme's badges block into the gallery, where custom-product.css pins it to the top left of the
  // carousel. The block stays on the partial-rerender list, so the theme replaces it where it now sits.
  const GALLERY_BADGES_SELECTOR = '.product-info__block-item[data-block-type="badges"]';
  const GALLERY_BADGES_TARGET_SELECTOR = ".product-gallery__image-list";

  const placeGalleryBadges = () => {
    const item = document.querySelector(GALLERY_BADGES_SELECTOR);
    const target = item?.closest(".product")?.querySelector(GALLERY_BADGES_TARGET_SELECTOR);

    // No badges block, no gallery on this product, or already in place.
    if (!target || item.parentElement === target) return;

    target.prepend(item);
  };

  placeGalleryBadges();
  document.addEventListener("custom:product-updated", placeGalleryBadges);
  // --- CUSTOM SCRIPT END: Badges over the media gallery ---

  // --- CUSTOM SCRIPT START: Color picker position in the product info column ---
  // Moves the stand-alone color row (custom liquid block, first in the column) above the variant-picker
  // block at >= 1000px and back to the top below. DOM move instead of CSS `order`, so the theme's collapsing
  // block margins and the reading order stay intact; only touched when the position is wrong (keeps focus).
  const COLOR_PICKER_SELECTOR = ".custom-color-picker";
  const COLOR_PICKER_LIST_SELECTOR = ".product-info__block-list";
  const COLOR_PICKER_SIZE_BLOCK_SELECTOR =
    '.product-info__block-item[data-block-type="variant-picker"]';
  const colorPickerTwoColumnLayout = window.matchMedia("(min-width: 1000px)");

  const placeColorPicker = () => {
    const item = document
      .querySelector(COLOR_PICKER_SELECTOR)
      ?.closest(".product-info__block-item");
    const list = item?.closest(COLOR_PICKER_LIST_SELECTOR);

    // No color row on this product (sets render nothing) or not a product page.
    if (!list) return;

    if (colorPickerTwoColumnLayout.matches) {
      const sizeBlock = list.querySelector(COLOR_PICKER_SIZE_BLOCK_SELECTOR);

      // Without a variant picker (single-size styles) there is nothing to sit above.
      if (sizeBlock && sizeBlock.previousElementSibling !== item) {
        sizeBlock.before(item);
      }
    } else if (list.firstElementChild !== item) {
      // Back below the gallery: the block is configured as the first block of the column.
      list.prepend(item);
    }
  };

  placeColorPicker();
  colorPickerTwoColumnLayout.addEventListener("change", placeColorPicker);
  document.addEventListener("custom:product-updated", placeColorPicker);
  // --- CUSTOM SCRIPT END: Color picker position in the product info column ---

  // --- CUSTOM SCRIPT START: Favorites heart in the title row ---
  // Moves the Klaviyo favorites app block into the title block, where custom-product.css lays out title and
  // heart as one row. DOM move instead of absolute positioning: the static position would depend on where the
  // merchant happens to place the block in the editor.
  const FAVORITES_ITEM_SELECTOR =
    '.product-info__block-item:is([data-block-id*="customer_hub_wishlist"], :has(button[class*="kl-hub-favorites"])):not(:has(.custom-product-title))';
  const FAVORITES_TITLE_SELECTOR =
    ".product-info__block-item:has(.custom-product-title)";

  const placeFavorites = () => {
    const item = document.querySelector(FAVORITES_ITEM_SELECTOR);
    const titleItem = document.querySelector(FAVORITES_TITLE_SELECTOR);

    // No favorites block, no custom title block, or already in place.
    if (!item || !titleItem || item.parentElement === titleItem) return;

    titleItem.append(item);
  };

  placeFavorites();
  document.addEventListener("custom:product-updated", placeFavorites);
  // --- CUSTOM SCRIPT END: Favorites heart in the title row ---

  // --- CUSTOM SCRIPT START: Sticky add-to-cart bar ---
  // For the mobile bar (custom-product.css pins the theme's buy-buttons block): publishes the measured bar
  // height as `--custom-sticky-atc-height` and moves the size selector out of the variant-picker block into
  // the bar below 700px, back above it. Sync runs on `custom:product-updated`, one frame after the DOM swap.
  const STICKY_ATC_MARKER_SELECTOR = ".custom-sticky-atc-enable";
  const STICKY_ATC_BAR_SELECTOR = '.product-info__block-item[data-block-type="buy-buttons"]';
  const STICKY_ATC_HEIGHT_VARIABLE = "--custom-sticky-atc-height";
  const STICKY_SIZE_SELECTOR = ".custom-sticky-size";
  // :not(quick-buy-modal *), because the popup renders the same block — only the main product's belongs in the bar.
  const STICKY_SIZE_BLOCK_SELECTOR =
    '.product-info__block-item[data-block-type="variant-picker"]:not(quick-buy-modal *)';
  // A selector that still sits where the server rendered it (inside the variant-picker block) is fresh.
  const STICKY_SIZE_FRESH_SELECTOR = `${STICKY_SIZE_BLOCK_SELECTOR} ${STICKY_SIZE_SELECTOR}`;
  const STICKY_SIZE_SHEET_SELECTOR = "x-popover.custom-sticky-size__sheet";
  const STICKY_SIZE_TRIGGER_SELECTOR = ".custom-sticky-size__trigger";
  const stickySizeInBar = window.matchMedia("(max-width: 699px)");

  // Marker absent (no custom liquid block) → feature is off, and nothing below needs to run.
  if (document.querySelector(STICKY_ATC_MARKER_SELECTOR)) {
    let observedStickyAtcBar = null;

    const publishStickyAtcHeight = () => {
      const bar = document.querySelector(STICKY_ATC_BAR_SELECTOR);

      // 0px above the mobile breakpoint, where the block sits in the column and needs no reservation.
      document.documentElement.style.setProperty(
        STICKY_ATC_HEIGHT_VARIABLE,
        bar ? `${bar.offsetHeight}px` : "0px"
      );
    };

    const stickyAtcResizeObserver = new ResizeObserver(publishStickyAtcHeight);

    // An open sheet was moved to <body> by the theme: hide() lets the theme re-append it into its wrapper,
    // even a detached one. Never remove the sheet element by hand.
    const closeStickySizeSheet = (element) => {
      const sheet = element?.querySelector(STICKY_SIZE_SHEET_SELECTOR);
      if (sheet && sheet.open) sheet.hide();
    };

    const placeStickySize = (bar) => {
      const fresh = document.querySelector(STICKY_SIZE_FRESH_SELECTOR);
      const mounted = bar
        ? Array.from(bar.children).find((child) => child.matches(STICKY_SIZE_SELECTOR))
        : null;

      // Above the breakpoint the selector belongs in the column, where the server rendered it. A copy in the
      // bar is either stale (a rerender delivered a fresh one) or has to move back across the breakpoint.
      if (!stickySizeInBar.matches) {
        if (!mounted) return;

        const cameFromBar = mounted.contains(document.activeElement);
        closeStickySizeSheet(mounted);

        if (fresh) {
          mounted.remove();
        } else {
          const picker = document.querySelector(`${STICKY_SIZE_BLOCK_SELECTOR} variant-picker`);
          if (!picker) return;
          picker.after(mounted);
        }

        if (cameFromBar) {
          document.querySelector(STICKY_SIZE_TRIGGER_SELECTOR)?.focus({ preventScroll: true });
        }
        return;
      }

      if (!fresh || !bar) return;

      const hadFocus = !!(mounted && mounted.contains(document.activeElement));

      if (mounted) {
        closeStickySizeSheet(mounted);
        mounted.remove();
      }

      // Hide footer buttons whose target dialog is not on this page (wrapper incl. icon, not just the button).
      fresh.querySelectorAll("[data-custom-sticky-open]").forEach((button) => {
        const wrapper = button.closest(".custom-sticky-size__fit") || button;
        wrapper.hidden = !document.getElementById(button.getAttribute("data-custom-sticky-open") || "");
      });

      const productForm = bar.querySelector("product-form");
      if (productForm) {
        bar.insertBefore(fresh, productForm);
      } else {
        bar.prepend(fresh);
      }

      if (hadFocus) fresh.querySelector(STICKY_SIZE_TRIGGER_SELECTOR)?.focus({ preventScroll: true });
    };

    const syncStickyAtcBar = () => {
      const bar = document.querySelector(STICKY_ATC_BAR_SELECTOR);

      placeStickySize(bar);

      if (bar !== observedStickyAtcBar) {
        if (observedStickyAtcBar) stickyAtcResizeObserver.unobserve(observedStickyAtcBar);
        observedStickyAtcBar = bar;
        if (bar) stickyAtcResizeObserver.observe(bar);
      }

      publishStickyAtcHeight();
    };

    syncStickyAtcBar();
    document.addEventListener("custom:product-updated", syncStickyAtcBar);
    stickySizeInBar.addEventListener("change", syncStickyAtcBar);

    // Open the fit-note modal ON TOP of the open sheet via show() — a synthetic click outside the sheet
    // would close it (focus traps stack, see Themes/MEMORY.md).
    document.addEventListener("click", (event) => {
      const origin = event.target instanceof Element ? event.target : null;
      const button = origin && origin.closest("[data-custom-sticky-open]");
      if (!button) return;

      const target = document.getElementById(button.getAttribute("data-custom-sticky-open") || "");
      if (!target || typeof target.show !== "function") return;

      target.show();
    });

    // After the sheet closes, focus the current trigger — the theme's return target was replaced by the
    // rerender. `dialog:after-hide` does not bubble → capture phase; one frame lets the trap finish first.
    document.addEventListener("dialog:after-hide", (event) => {
      const sheet = event.target;
      if (!(sheet instanceof Element) || !sheet.matches(STICKY_SIZE_SHEET_SELECTOR)) return;

      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active && active !== document.body && active.isConnected) return;

        const bar = document.querySelector(STICKY_ATC_BAR_SELECTOR);
        bar?.querySelector(STICKY_SIZE_TRIGGER_SELECTOR)?.focus({ preventScroll: true });
      });
    }, true);
  }
  // --- CUSTOM SCRIPT END: Sticky add-to-cart bar ---

  // --- CUSTOM SCRIPT START: Single-open accordions in the product info column ---
  // Opening one product accordion closes its open siblings. Trigger is the `aria-expanded` state (covers
  // pointer, keyboard and the theme editor); closing runs through the element's own close(). Scope = every
  // container the theme renders product accordions into; footer and FAQ accordions are unaffected.
  const PRODUCT_ACCORDION_SCOPE_SELECTOR =
    ".accordion-group, .product-content-below-gallery, .product-info__block-list";

  const closeSiblingAccordions = (details) => {
    const current = details.closest("accordion-disclosure");
    const scope = current && current.closest(PRODUCT_ACCORDION_SCOPE_SELECTOR);

    if (!scope) return;

    scope.querySelectorAll("accordion-disclosure").forEach((other) => {
      if (other === current || typeof other.close !== "function") return;
      if (other.disclosureElement?.getAttribute("aria-expanded") === "true") other.close();
    });
  };

  // Observed on body so re-rendered product markup stays covered.
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const details = mutation.target;

      if (
        details instanceof Element &&
        details.matches("details[aria-expanded='true']")
      ) {
        closeSiblingAccordions(details);
      }
    });
  }).observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-expanded"],
  });
  // --- CUSTOM SCRIPT END: Single-open accordions in the product info column ---
})();
